/**
 * PreviewTab Component (T088, T092, T095, T096, T099, T110)
 * Renders provider profile preview exactly as it will appear to end users
 * 
 * Features:
 * - Transforms form data to provider format for preview
 * - Renders using exact ProviderDetails components (design consistency)
 * - Shows unsaved changes banner when form is dirty (T092)
 * - Displays media gallery with ordering and is_primary (T095)
 * - Excludes media from inline editing (T096 - no inline edit in preview)
 * - Optimized with React.memo and useMemo (T099)
 * - Sync time <300ms (SC-008 performance requirement)
 * - Inline editing support (T110 - see INLINE_EDIT_INTEGRATION note below)
 * 
 * @note INLINE_EDIT_INTEGRATION (T110):
 * Inline editing in PreviewTab requires wrapping editable fields with InlineEditWrapper.
 * However, ProviderDetails components are from provider-directory feature and should
 * remain independent. To add inline editing:
 * 
 * Option 1 (Recommended): Create admin-specific wrapper components that:
 *   - Import ProviderDetails components
 *   - Wrap specific display elements with InlineEditWrapper
 *   - Pass onSave callback to update form state
 * 
 * Option 2: Add optional inline edit props to ProviderDetails components
 *   - Requires modifying provider-directory feature
 *   - Better encapsulation but creates feature coupling
 * 
 * Example implementation pattern:
 * ```typescript
 * // AdminProviderDetailsHero.tsx
 * import { ProviderDetailsHero } from '@/features/provider-directory/components/ProviderDetailsHero';
 * import { InlineEditWrapper } from '../inline-edit/InlineEditWrapper';
 * import { InlineTextField } from '../inline-edit/InlineTextField';
 * 
 * export function AdminProviderDetailsHero({ provider, onFieldUpdate }) {
 *   return (
 *     <div>
 *       <InlineEditWrapper
 *         value={provider.companyName}
 *         onSave={(field, value) => onFieldUpdate(field, value)}
 *         fieldName="companyName"
 *         displayComponent={({ value }) => <h1>{value}</h1>}
 *         editComponent={InlineTextField}
 *       />
 *       {/* Other hero content... *\/}
 *     </div>
 *   );
 * }
 * ```
 * 
 * Current implementation: Preview tab uses ProviderDetails as-is (view-only).
 * Inline editing components are implemented and tested (T100-T109).
 * Integration requires architectural decision on wrapping strategy.
 */

import { memo, useMemo } from 'react';
import { transformFormDataToProvider } from './utils/transformers';
import { UnsavedChangesBanner } from './UnsavedChangesBanner';
import { AdminProviderDetailsHero } from './admin-wrappers/AdminProviderDetailsHero';
import { AdminProviderDetailsOverview } from './admin-wrappers/AdminProviderDetailsOverview';
import { ProviderDetailsServices } from '@/features/provider-directory/components/ProviderDetailsServices';
import { ProviderDetailsQualifications } from '@/features/provider-directory/components/ProviderDetailsQualifications';
import { ProviderDetailsReviews } from '@/features/provider-directory/components/ProviderDetailsReviews';
import { ProviderDetailsResources } from '@/features/provider-directory/components/ProviderDetailsResources';
import { ProviderDetailsCareDelivery } from '@/features/provider-directory/components/ProviderDetailsCareDelivery';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useCategories, useSpecialties, useExpertise } from '../../hooks/useTaxonomy';
import type { ProviderEditorFormData } from './schemas';
import { getPreviewSections, getDefaultPreviewTab } from './utils/getPreviewSections';

type PreviewTabProps = {
  /** Current form data to preview */
  formData: ProviderEditorFormData;
  
  /** Whether form has unsaved changes */
  isDirty: boolean;
  
  /** Callback to update individual fields inline */
  onFieldUpdate: (fieldName: string, value: string) => Promise<void>;

  /** SQL-only The Key intake URL — not in form state, needed for Schedule Call in preview */
  intakeFormUrl?: string | null;
};

/**
 * PreviewTab Component
 * 
 * Renders exact preview of how provider profile will appear to end users
 * Uses actual ProviderDetails components to guarantee visual fidelity
 * 
 * Performance optimizations (T099):
 * - Memoized with React.memo to prevent unnecessary re-renders
 * - Memoized provider transformation with useMemo
 * - Only re-transforms when formData changes
 * 
 * @example
 * <PreviewTab formData={formState} isDirty={formState.isDirty} />
 */
export const PreviewTab = memo(({ formData, isDirty, onFieldUpdate, intakeFormUrl = null }: PreviewTabProps) => {
  // Load reference data for taxonomy lookups (categories, specialties, expertise)
  const { data: categories = [] } = useCategories();
  const { data: specialties = [] } = useSpecialties();
  const { data: expertise = [] } = useExpertise();

  // T099: Memoize provider transformation for performance
  // Only recalculate when formData or reference data changes
  const provider = useMemo(() => {
    return transformFormDataToProvider(formData, {
      categories,
      specialties,
      expertise,
      intakeFormUrl,
    });
  }, [formData, categories, specialties, expertise, intakeFormUrl]);

  // Format service ranges for display
  const serviceRangesText = provider.serviceRanges
    ?.map(range => {
      switch (range) {
        case 'IN_HOME': return 'In-Home';
        case 'VIRTUAL': return 'Virtual';
        case 'IN_OFFICE': return 'In-Office';
        default: return range;
      }
    })
    .join(' • ') || '';

  // Get available sections based on data (centralized configuration)
  const availableSections = useMemo(() => {
    return getPreviewSections(provider);
  }, [provider]);

  // Get default active tab (first available section)
  const defaultTab = useMemo(() => {
    return getDefaultPreviewTab(availableSections);
  }, [availableSections]);

  return (
    <div className="space-y-6" role="tabpanel" aria-label="Preview Tab">
      {/* T092: Unsaved Changes Banner */}
      {isDirty && <UnsavedChangesBanner />}

      {/* Hero Section - Admin wrapper with inline editing */}
      <AdminProviderDetailsHero
        provider={provider}
        displayRating={provider.averageRating}
        displayReviewCount={provider.totalReviewCount}
        reviewsLoading={false}
        serviceRangesText={serviceRangesText}
        canFavorite={false}
        canAddToNetwork={false}
        currentFavoriteState={false}
        currentNetworkState={false}
        onFavoriteToggle={() => {}}
        onNetworkToggle={() => {}}
        onMessageProvider={() => {}}
        onScheduleCall={() => {}}
        onShareProvider={() => {}}
        isFavoriteLoading={false}
        isNetworkLoading={false}
        onFieldUpdate={onFieldUpdate}
      />

      {/* Provider Details Tabs - Sections rendered based on configuration */}
      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="w-full justify-start border-b border-gray-200 bg-transparent">
          {availableSections.map(section => (
            <TabsTrigger
              key={section.id}
              value={section.id}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#026857] data-[state=active]:bg-transparent"
            >
              {section.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {availableSections.map(section => {
          // Render section content based on section ID
          let content;

          switch (section.id) {
            case 'overview':
              content = (
                <AdminProviderDetailsOverview
                  provider={provider}
                  serviceRangesText={serviceRangesText}
                  onFieldUpdate={onFieldUpdate}
                />
              );
              break;

            case 'services':
              content = <ProviderDetailsServices provider={provider} />;
              break;

            case 'care-delivery':
              content = (
                <ProviderDetailsCareDelivery
                  priceDetails={provider.priceDetailsText}
                  carePhilosophy={provider.carePhilosophy}
                  emergencyResponseDetails={provider.emergencyResponseDetails}
                />
              );
              break;

            case 'qualifications':
              content = <ProviderDetailsQualifications provider={provider} />;
              break;

            case 'resources':
              // Feature 011 - US1: Resources tab (includes photos AND authored resources)
              content = (
                <ProviderDetailsResources
                  providerId={provider.id}
                  photos={provider.media || []}
                  documents={provider.documents ?? []}
                />
              );
              break;

            case 'reviews':
              content = <ProviderDetailsReviews provider={provider} />;
              break;

            default:
              content = null;
          }

          return (
            <TabsContent key={section.id} value={section.id} className="mt-6">
              {content}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
});

PreviewTab.displayName = 'PreviewTab';

